import { ROLE } from "@/providers/AuthorizationProvider/types";

export const AMPLITUDE_USER_TYPE_PROPERTY = "m360_user_type";
export const LEGACY_USER_TYPE_PROPERTY = "user_type";

export const AMPLITUDE_USER_TYPES = {
  HQ_STAFF: "hq_staff",
  SECTION_STAFF: "section_staff",
  UNIVERSITY_ADMIN: "university_admin"
} as const;

export type AmplitudeUserType = (typeof AMPLITUDE_USER_TYPES)[keyof typeof AMPLITUDE_USER_TYPES];

export type AffiliationRecord = {
  id?: string | null;
  legacyId?: string | null;
  name?: string | null;
};

export type AffiliationIdentifyProperties = {
  m360_user_type: AmplitudeUserType;
  section_name?: string | string[];
  university_id?: string | string[];
  university_name?: string | string[];
};

export function resolveAmplitudeUserType(roles: readonly string[] | null | undefined): AmplitudeUserType {
  const assignedRoles = new Set(roles ?? []);

  if (assignedRoles.has(ROLE.UNIVERSITY_STAFF)) {
    return AMPLITUDE_USER_TYPES.UNIVERSITY_ADMIN;
  }

  if (assignedRoles.has(ROLE.SECTION_STAFF) || assignedRoles.has(ROLE.SECTION_ADMIN)) {
    return AMPLITUDE_USER_TYPES.SECTION_STAFF;
  }

  return AMPLITUDE_USER_TYPES.HQ_STAFF;
}

export function matchAffiliationNames(
  assignedIds: readonly string[],
  records: readonly AffiliationRecord[] | undefined
): string[] {
  if (assignedIds.length === 0 || !records?.length) {
    return [];
  }

  const assigned = new Set(assignedIds);
  const names: string[] = [];

  for (const record of records) {
    const matchesAssignedId =
      (record.legacyId != null && assigned.has(record.legacyId)) || (record.id != null && assigned.has(record.id));
    if (matchesAssignedId && record.name) {
      names.push(record.name);
    }
  }

  return names;
}

export function identifyScalarOrList(values: readonly string[]): string | string[] | undefined {
  if (values.length === 0) {
    return undefined;
  }

  if (values.length === 1) {
    return values[0];
  }

  return [...values];
}

const OPTIONAL_AFFILIATION_PROPERTIES = ["section_name", "university_id", "university_name"] as const;

export function setAffiliationIdentifyProperties(
  identify: {
    set: (property: string, value: string | string[]) => unknown;
    unset: (property: string) => unknown;
  },
  properties: AffiliationIdentifyProperties
) {
  identify.set(AMPLITUDE_USER_TYPE_PROPERTY, properties.m360_user_type);
  identify.unset(LEGACY_USER_TYPE_PROPERTY);
  for (const property of OPTIONAL_AFFILIATION_PROPERTIES) {
    const value = properties[property];
    if (value !== undefined) {
      identify.set(property, value);
    } else {
      identify.unset(property);
    }
  }
}

export function buildAffiliationIdentifyProperties({
  roles,
  sectionLegacyIds,
  universityLegacyIds,
  sections,
  universities
}: {
  roles: readonly string[];
  sectionLegacyIds: readonly string[];
  universityLegacyIds: readonly string[];
  sections?: readonly AffiliationRecord[];
  universities?: readonly AffiliationRecord[];
}): AffiliationIdentifyProperties {
  const userType = resolveAmplitudeUserType(roles);
  const properties: AffiliationIdentifyProperties = { m360_user_type: userType };

  if (userType === AMPLITUDE_USER_TYPES.SECTION_STAFF) {
    const sectionName = identifyScalarOrList(matchAffiliationNames(sectionLegacyIds, sections));
    if (sectionName !== undefined) {
      properties.section_name = sectionName;
    }
  }

  if (userType === AMPLITUDE_USER_TYPES.UNIVERSITY_ADMIN) {
    const universityId = identifyScalarOrList(universityLegacyIds);
    if (universityId !== undefined) {
      properties.university_id = universityId;
    }

    const universityName = identifyScalarOrList(matchAffiliationNames(universityLegacyIds, universities));
    if (universityName !== undefined) {
      properties.university_name = universityName;
    }
  }

  return properties;
}
